
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from './AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      // Navigate to home page to trigger role-based redirect
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to log in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      padding: '2rem 0'
    }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={5} lg={4}>
            {/* Logo Section */}
            <div className="text-center mb-5">
              <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
                <img
                  src="/Access70Logo.png"
                  alt="Access70"
                  style={{ height: '50px', width: 'auto' }}
                />
                <img
                  src="/NPI_Logo.png"
                  alt="NPI Governance Solutions"
                  style={{ height: '65px', width: 'auto' }}
                />
              </div>
              <h1 style={{
                color: 'var(--primary-blue)',
                fontWeight: 'var(--font-weight-extrabold)',
                fontSize: '28px',
                marginBottom: '8px'
              }}>
                Access70 LMS
              </h1>
              <p style={{
                color: 'var(--gray-text)',
                fontSize: '16px',
                fontWeight: 'var(--font-weight-medium)'
              }}>
                Learning Management System
              </p>
            </div>

            {/* Login Form */}
            <Card style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              background: 'var(--gradient-blue)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background Pattern for Form */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  linear-gradient(45deg, transparent 40%, rgba(0, 177, 255, 0.1) 41%, rgba(0, 177, 255, 0.1) 43%, transparent 44%),
                  linear-gradient(-45deg, transparent 40%, rgba(0, 177, 255, 0.1) 41%, rgba(0, 177, 255, 0.1) 43%, transparent 44%)
                `,
                backgroundSize: '60px 60px',
                opacity: 0.3,
                pointerEvents: 'none'
              }} />

              <Card style={{
                borderRadius: '16px',
                border: 'none',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                margin: '20px',
                position: 'relative',
                zIndex: 2
              }}>
              <Card.Body style={{ padding: '2.5rem' }}>
                <div className="text-center mb-4">
                  <h3 style={{
                    color: 'var(--primary-blue)',
                    fontWeight: 'var(--font-weight-bold)',
                    marginBottom: '0.5rem'
                  }}>
                    Welcome Back
                  </h3>
                  <p style={{
                    color: 'var(--gray-text)',
                    fontSize: '14px'
                  }}>
                    Sign in to access your learning dashboard
                  </p>
                </div>

                {error && <Alert variant="danger" style={{ borderRadius: '12px' }}>{error}</Alert>}

                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--black)',
                      marginBottom: '8px'
                    }}>
                      Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      style={{
                        borderRadius: '12px',
                        border: '2px solid var(--gray-border)',
                        padding: '12px 16px',
                        fontSize: '16px',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={{
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--black)',
                      marginBottom: '8px'
                    }}>
                      Password
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        style={{
                          borderRadius: '12px',
                          border: '2px solid var(--gray-border)',
                          padding: '12px 48px 12px 16px',
                          fontSize: '16px',
                          transition: 'all 0.3s ease'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          color: 'var(--gray-text)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-blue)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--gray-text)'}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                  </Form.Group>

                  <Button
                    className="w-100"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    style={{
                      background: 'var(--gradient-primary)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '14px',
                      fontSize: '16px',
                      fontWeight: 'var(--font-weight-semibold)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 8px 20px rgba(90, 0, 163, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Form>
              </Card.Body>
              </Card>
            </Card>

            {/* Info Card */}
            <Card className="mt-4" style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
              background: 'var(--gradient-blue)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background Pattern for Info Card */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  linear-gradient(45deg, transparent 40%, rgba(0, 177, 255, 0.1) 41%, rgba(0, 177, 255, 0.1) 43%, transparent 44%),
                  linear-gradient(-45deg, transparent 40%, rgba(0, 177, 255, 0.1) 41%, rgba(0, 177, 255, 0.1) 43%, transparent 44%)
                `,
                backgroundSize: '60px 60px',
                opacity: 0.3,
                pointerEvents: 'none'
              }} />

              <Card.Body style={{ padding: '1.5rem', position: 'relative', zIndex: 2 }}>
                <div className="text-center">
                  <small style={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    fontWeight: 'var(--font-weight-medium)'
                  }}>
                    🔐 Secure access required<br/>
                    Only accounts created by administrators can sign in.<br/>
                    Contact your administrator for access credentials.
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage;
